import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import type { NotificationPrefs } from '@/lib/api';
import { CenteredSpinner, ErrorState } from '@/components/ui';
import { useNotificationPrefs, useSetNotificationPrefs } from '@/lib/queries';
import { cn } from '@/lib/format';

/**
 * Settings — currently just email-notification preferences. Each toggle saves
 * immediately (optimistically) so there's no separate save button to forget.
 */
const TOGGLES: { key: keyof NotificationPrefs; title: string; desc: string }[] = [
  { key: 'onAssigned', title: 'Zuweisungen', desc: 'E-Mail, wenn dir eine Aufgabe zugewiesen wird.' },
  { key: 'onComment', title: 'Kommentare', desc: 'E-Mail bei neuen Kommentaren zu deinen Aufgaben.' },
  { key: 'dueDigest', title: 'Tägliche Fälligkeiten', desc: 'Morgens eine Übersicht deiner heute fälligen und überfälligen Aufgaben.' },
  { key: 'dueSoon', title: 'Bald fällig', desc: 'Hinweis am Vortag zu Aufgaben, die morgen fällig sind.' },
];

export function SettingsPage() {
  const { data, isPending, error, refetch } = useNotificationPrefs();
  const save = useSetNotificationPrefs();
  const [local, setLocal] = useState<NotificationPrefs | null>(null);
  const [testState, setTestState] = useState<string | null>(null);

  useEffect(() => {
    if (data) setLocal(data);
  }, [data]);

  if (isPending || !local) return <CenteredSpinner label="Einstellungen werden geladen…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  function toggle(key: keyof NotificationPrefs) {
    const next = { ...(local as NotificationPrefs), [key]: !local![key] };
    setLocal(next); // optimistic
    save.mutate(next, { onError: () => setLocal(local) }); // revert on failure
  }

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-2xl px-6 py-6">
        <div className="mb-5 flex items-center gap-2">
          <Bell className="size-5 text-brand-500" aria-hidden />
          <h1 className="text-lg font-semibold text-strong">E-Mail-Benachrichtigungen</h1>
        </div>

        <div className="divide-y line rounded-[var(--radius-card)] surface-card border line">
          {TOGGLES.map(({ key, title, desc }) => (
            <label key={key} className="flex cursor-pointer items-start gap-3 px-4 py-3.5">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-strong">{title}</div>
                <div className="mt-0.5 text-xs text-muted">{desc}</div>
              </div>
              {/* Accessible switch: a real checkbox styled as a toggle. */}
              <button
                type="button"
                role="switch"
                aria-checked={local[key]}
                aria-label={title}
                onClick={() => toggle(key)}
                className={cn(
                  'relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors',
                  local[key] ? 'bg-brand-500' : 'bg-[var(--line-strong)]',
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 size-4 rounded-full bg-white shadow transition-transform',
                    local[key] ? 'translate-x-[18px]' : 'translate-x-0.5',
                  )}
                />
              </button>
            </label>
          ))}
        </div>

        <p className="mt-3 text-xs text-faint">
          Änderungen werden sofort gespeichert. Standardmäßig sind alle Benachrichtigungen aktiv.
        </p>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={async () => {
              setTestState('sending');
              try {
                const r = await import('@/lib/api').then((m) => m.api.sendTestEmail());
                setTestState(`✓ Test an ${r.to} gesendet (falls Versand aktiv)`);
              } catch (e) {
                setTestState(`✗ ${e instanceof Error ? e.message : 'Fehler'}`);
              }
            }}
            className="rounded-lg border line surface-card px-3 py-1.5 text-xs font-medium text-body hover:surface-hover"
          >
            Test-E-Mail an mich senden
          </button>
          {testState && <span className="text-xs text-muted">{testState}</span>}
        </div>
      </div>
    </div>
  );
}
